import { useFilterContext } from "../../../controls/FilterContext/FilterContext";
import { FlatOfficeItem } from "./FlatOfficeItem";
import { LocationVariants } from "./utils";
import { LabelWithHighlight } from "../../../data-display/labels/LabelWithHighlight/LabelWithHighlight";
import {
  ExtendedDivisionTree,
  NodeId,
  OfficeSelectContentProps,
  PossibleDivisionNode
} from "./OfficeSelect";
import { ChevronLeft } from "../../../icons/TacticIcons/ChevronLeft";
import { ChevronRight } from "../../../icons/TacticIcons/ChevronRight";
import { DivisionsTree } from "@gettactic/api";
import { isWorkingLocationOffice } from "@gettactic/helpers/src/resources/utils";
import { Button } from "@v2/components/ui/button";
import { classNames, cn } from "@gettactic/helpers";
/**
 * A helper function that counts all offices inside a subtree,
 * including nested children.
 */
function countOfficesRecursive(node: PossibleDivisionNode): number {
  let total = 0;

  // Count the offices in the current node (if node.offices exists)
  if (node.offices?.length) {
    total += node.offices.length;
  }

  // Recursively count offices in children
  if (node.children?.length) {
    for (const child of node.children) {
      total += countOfficesRecursive(child);
    }
  }

  return total;
}

export function OfficeSelectContent({
  allItems,
  currentDir,
  onOpenDir,
  onBack,
  setNoFilteredResults,
}: OfficeSelectContentProps) {
  const { filter: locationFilter, resetFilter } = useFilterContext();
  const isFilterEmpty = !locationFilter;

  if (isFilterEmpty) {
    // 1a) Not in a subdir => top-level
    if (!currentDir) {
      return (
        <>
          {allItems.map((node) => {
            const isOffice = isWorkingLocationOffice(node.id);
            if (isOffice) {
              return (
                <FlatOfficeItem
                  key={node.id}
                  value={node.id.toString()}
                  type={LocationVariants.office}
                  label={node.name}
                  locationFilter={locationFilter}
                  total_desks={(node as ExtendedDivisionTree).total_desks}
                  total_open_desks={
                    (node as ExtendedDivisionTree).total_open_desks
                  }
                />
              );
            }

            // It's a directory
            const totalOffices = countOfficesRecursive(node);
            const hasChildren = (node.children?.length || 0) > 0;
            return (
              <div
                key={node.id}
                // pointer move trigger focus by default, for now let's keep default behavior
                // onPointerLeave={(event) => event.preventDefault()}
                // onPointerMove={(event) => event.preventDefault()}
                onClick={
                  hasChildren || totalOffices
                    ? () => onOpenDir(node.id)
                    : undefined
                }
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between px-4 py-2 text-sm",
                  "hover:bg-muted focus:rounded-sm focus:bg-muted"
                )}
              >
                <div className={"min-w-0 flex-1 break-words whitespace-normal pr-2"}>
                  <LabelWithHighlight
                    label={`${node.name} (${totalOffices})`}
                    highlight=""
                  />
                </div>
                <div className="">
                  {(hasChildren || totalOffices > 0) && <ChevronRight />}
                </div>
              </div>
            );
          })}
        </>
      );
    }

    // We are inside a directory => show children + back
    return renderDirContents(currentDir, onBack, onOpenDir, locationFilter);
  }

  // Filter is active
  if (!currentDir) {
    // We are not inside any subdir => show matching dirs/offices
    return renderFilteredResults(
      allItems,
      locationFilter,
      onOpenDir,
      resetFilter,
      setNoFilteredResults,
    );
  }
  // We are inside a dir => show *all* children of that dir ignoring filter
  return renderDirContents(currentDir, onBack, onOpenDir, locationFilter);
}

type OfficeNode = {
  id: NodeId;
  name: string;
  total_desks: number;
  total_open_desks: number;
};

/**
 * A small helper that returns all offices (flattened) that match the locationFilter.
 */
function getFilteredOffices(
  allItems: PossibleDivisionNode[],
  filter: string
): OfficeNode[] {
  const result: OfficeNode[] = [];
  const lower = filter.toLowerCase();

  function dfs(node: PossibleDivisionNode) {
    // Case A: If “node” itself is a pure office node
    if (!isDirectory(node)) {
      // Then “node.name” is the office’s name
      if (node.name.toLowerCase().includes(lower)) {
        // We'll treat ExtendedDivisionTree as an OfficeNode
        result.push({
          id: node.id,
          name: node.name,
          total_desks: (node as ExtendedDivisionTree).total_desks,
          total_open_desks: (node as ExtendedDivisionTree).total_open_desks
        });
      }
    }

    // Case B: If node.offices exist => filter them
    node.offices?.forEach((office) => {
      if (office.name.toLowerCase().includes(lower)) {
        result.push({
          id: office.id,
          name: office.name,
          total_desks: office.total_desks,
          total_open_desks: office.total_open_desks
        });
      }
    });

    // Case C: Recurse into children
    node.children?.forEach(dfs);
  }

  allItems.forEach(dfs);
  return result;
}
function renderFilteredResults(
  allItems: (ExtendedDivisionTree | DivisionsTree)[],
  locationFilter: string,
  onOpenDir: (dirId: string) => void,
  onClearFilter: () => void,
  setNoFilteredResults: (value: boolean) => void,
) {
  const matchedDirs = getFilteredDirs(allItems, locationFilter);
  const matchedOffices = getFilteredOffices(allItems, locationFilter);

  if (!matchedOffices?.length) {
    setNoFilteredResults(true);
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Button
          className="text-sm text-neutral-500 !my-0 !py-0"
          variant="link"
          onClick={onClearFilter}
        >
          Clear the filter
        </Button>
      </div>
    );
  }
  setNoFilteredResults(false);
  return (
    <>
      {matchedDirs.map((dir) => (
        <div
          key={dir.id}
          onClick={() => onOpenDir(dir.id)}
          className={cn(
            "flex cursor-pointer items-center justify-between px-4 py-2 text-sm",
            "focus:rounded-sm focus:bg-muted"
          )}
        >
          <div className={"min-w-0 flex-1 break-words whitespace-normal pr-2"}>
            <LabelWithHighlight label={dir.name} highlight={locationFilter} />
          </div>
          <ChevronRight />
        </div>
      ))}

      {matchedOffices.map((office) => (
        <FlatOfficeItem
          key={`flat-office-${office.id}`}
          value={office.id.toString()}
          type={LocationVariants.office}
          label={office.name}
          locationFilter={locationFilter}
          total_desks={office.total_desks}
          total_open_desks={office.total_open_desks}
        >
          <LabelWithHighlight label={office.name} highlight={locationFilter} />
        </FlatOfficeItem>
      ))}
    </>
  );
}

/**
 * Returns all directories that have a match somewhere in them
 * or whose own name matches the filter.
 */
function getFilteredDirs(
  allItems: PossibleDivisionNode[],
  filter: string
): PossibleDivisionNode[] {
  const lower = filter.toLowerCase();
  const result: PossibleDivisionNode[] = [];

  function recurse(node: PossibleDivisionNode) {
    // We only push this node if *its own* name matches
    // and it’s effectively a directory (children or offices).
    if (isDirectory(node) && node.name.toLowerCase().includes(lower)) {
      result.push(node);
    }
    // Recurse deeper to find other matching directories
    node.children?.forEach(recurse);
  }

  allItems.forEach(recurse);
  return result;
}

function renderDirContents(
  dir: PossibleDivisionNode,
  onBack: () => void,
  onOpenDir: (dirId: NodeId) => void,
  locationFilter: string
) {
  return (
    <>
      {/* "Back" item */}
      <div
        onClick={onBack}
        className="flex w-full cursor-pointer items-center p-2 pl-4 text-sm hover:bg-muted"
      >
        <ChevronLeft />
        <LabelWithHighlight label={dir.name} highlight={locationFilter} />
      </div>

      {/* Render sub-directories first */}
      {dir.children?.map((childDir) => {
        return (
          <SubDir
            key={childDir.id}
            childDir={childDir}
            onOpenDir={onOpenDir}
            locationFilter={locationFilter}
          />
        );
      })}

      {dir.offices?.map((office) => (
        <FlatOfficeItem
          key={`flat-office-${office.id}`}
          value={office.id.toString()}
          type={LocationVariants.office}
          label={office.name}
          locationFilter={locationFilter}
          total_desks={office.total_desks}
          total_open_desks={office.total_open_desks}
        >
          <LabelWithHighlight label={office.name} highlight="" />
        </FlatOfficeItem>
      ))}
    </>
  );
}

function isDirectory(node: PossibleDivisionNode) {
  return Boolean(node.children?.length || node.offices?.length);
}

const SubDir = ({
  childDir,
  onOpenDir,
  locationFilter
}: {
  childDir: PossibleDivisionNode;
  onOpenDir: (dirId: NodeId) => void;
  locationFilter: string | undefined;
}) => {
  if (!childDir) return null;
  const totalOffices = countOfficesRecursive(childDir);
  return (
    <div
      key={childDir.id}
      // pointer move trigger focus by default, for now let's keep default behavior
      // onPointerLeave={(event) => event.preventDefault()}
      // onPointerMove={(event) => event.preventDefault()}
      onClick={() => onOpenDir(childDir.id)}
      className={classNames(
        "flex cursor-pointer items-center justify-between p-2 px-4 text-sm",
        "hover:bg-muted focus:rounded-sm focus:bg-muted"
      )}
    >
      <div className={"min-w-0 flex-1 break-words whitespace-normal pr-2"}>
        <LabelWithHighlight
          label={`${childDir.name} (${totalOffices})`}
          highlight={locationFilter}
        />
      </div>
      <ChevronRight />
    </div>
  );
};
